/* iter55z-k — StripeModeBanner: slim status banner shown at the top of
 * the admin console.
 *
 * States (ordered from safest → riskiest):
 *   1. LIVE + webhook + publishable         → green   "Live mode — payments enabled"
 *   2. LIVE + missing webhook secret        → amber   "Live but webhook not verified"
 *   3. LIVE + missing publishable key       → amber   "Live but frontend can't collect cards"
 *   4. TEST                                 → sky     "Test mode — no real charges"
 *   5. UNKNOWN / no secret                  → red     "Stripe not configured"
 *
 * Fetches `/api/payments/config` once on mount and polls every 60s so
 * admins see rotations without a page refresh.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCircle2, AlertTriangle, XCircle, Rocket } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const POLL_MS = 60_000;

export default function StripeModeBanner() {
  const [cfg, setCfg] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/payments/config`);
      setCfg(r.data);
    } catch {
      setCfg({ mode: 'unknown', has_secret: false });
    }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, POLL_MS);
    return () => clearInterval(iv);
  }, [load]);

  if (!cfg) return null;

  const { mode, has_secret, has_publishable_key, has_webhook_secret,
          detected_mode, declared_mode } = cfg;

  // Highest-severity warning wins.
  let severity = 'info';        // info | ok | warn | error
  let icon     = <Rocket className="h-3.5 w-3.5" />;
  let text     = 'Stripe mode: unknown';
  let hint     = '';

  if (!has_secret) {
    severity = 'error';
    icon     = <XCircle className="h-3.5 w-3.5" />;
    text     = 'Stripe not configured';
    hint     = 'Set STRIPE_API_KEY in the deployment env vars to enable payments.';
  } else if (mode === 'live') {
    if (!has_webhook_secret) {
      severity = 'warn';
      icon     = <AlertTriangle className="h-3.5 w-3.5" />;
      text     = 'Live mode — webhook NOT verified';
      hint     = 'STRIPE_WEBHOOK_SECRET is missing. Card charges will succeed but webhooks are rejected.';
    } else if (!has_publishable_key) {
      severity = 'warn';
      icon     = <AlertTriangle className="h-3.5 w-3.5" />;
      text     = 'Live mode — publishable key missing';
      hint     = 'Set STRIPE_PUBLISHABLE_KEY (and REACT_APP_STRIPE_PUBLISHABLE_KEY at build time) so the frontend Elements checkout can collect cards.';
    } else {
      severity = 'ok';
      icon     = <CheckCircle2 className="h-3.5 w-3.5" />;
      text     = 'Live mode — payments enabled';
      hint     = 'Real cards will be charged. Rotate keys via Stripe Dashboard if compromised.';
    }
  } else if (mode === 'test') {
    severity = 'info';
    icon     = <Rocket className="h-3.5 w-3.5" />;
    text     = 'Test mode — no real charges';
    hint     = 'Use Stripe test cards (e.g. 4242 4242 4242 4242) to simulate flows.';
  } else if (detected_mode !== declared_mode && declared_mode) {
    severity = 'warn';
    icon     = <AlertTriangle className="h-3.5 w-3.5" />;
    text     = `STRIPE_MODE=${declared_mode} but key looks ${detected_mode}`;
    hint     = 'Fix the mismatch — Stripe will reject calls when mode and key type disagree.';
  }

  const tone =
    severity === 'ok'    ? 'border-emerald-400/60 bg-emerald-50 text-emerald-900' :
    severity === 'warn'  ? 'border-amber-400/60 bg-amber-50 text-amber-900' :
    severity === 'error' ? 'border-red-400/60 bg-red-50 text-red-900' :
                           'border-sky-300/60 bg-sky-50 text-sky-900';

  return (
    <div
      data-testid="admin-stripe-mode-banner"
      data-mode={mode}
      data-severity={severity}
      className={`flex items-center gap-2 border-b px-4 py-1.5 text-[11.5px] ${tone}`}
    >
      <span className="shrink-0">{icon}</span>
      <span className="font-semibold" data-testid="admin-stripe-mode-label">{text}</span>
      {hint && (
        <span className="hidden text-[11px] opacity-80 sm:inline" data-testid="admin-stripe-mode-hint">
          · {hint}
        </span>
      )}
      <span className="ml-auto flex items-center gap-2 text-[10px] uppercase tracking-wider opacity-75">
        <span>webhook: {has_webhook_secret ? '✓' : '—'}</span>
        <span>pk: {has_publishable_key ? '✓' : '—'}</span>
        <span>secret: {has_secret ? '✓' : '—'}</span>
      </span>
    </div>
  );
}
